import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Circle,
  Crosshair,
  MapPinned,
  Pentagon,
  Plus,
  Radar,
  Search,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { Circle as GoogleCircle, DrawingManager, GoogleMap, Polygon, useJsApiLoader } from "@react-google-maps/api";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import { logout } from "../../store/authSlice";
import { useStatus } from "../../popup/NotificationContext";
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_VERSION } from "../../config/googleMaps";
import {
  useAssignSharedGeofenceDeviceMutation,
  useCreateSharedGeofenceMutation,
  useDeleteSharedGeofenceMutation,
  useGetAllLiteDevicesQuery,
  useGetSharedGeofencesQuery,
  useUnassignSharedGeofenceDeviceMutation,
  useUpdateSharedGeofenceMutation,
} from "../../redux/admin/adminApi";
import { useGetAlarmsQuery } from "../../redux/admin/alarmApi";
import { getCanonicalVehicleStatus } from "../../utils/vehicleStatus";
import "../admin/dashboard-redesign.css";
import "./GeofencesPage.css";

const DEFAULT_CENTER = { lat: 20, lng: 0 };
const GEOFENCE_ALARM_TYPES = ["geofence_entry", "geofence_exit", "geofence_violation", "enter_area", "exit_area"];
const SHAPE_OPTIONS = {
  fillColor: "#245ee8",
  fillOpacity: 0.2,
  strokeColor: "#1d4ed8",
  strokeWeight: 2,
  editable: true,
  draggable: true,
};
const safeLower = (value) => String(value || "").toLowerCase();

const formatAlarmLabel = (value) =>
  String(value || "unknown")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No timestamp";
  return date.toLocaleString();
};

const normalizeAssignedDeviceIds = (item = {}) => {
  if (Array.isArray(item?.assignedDeviceIds)) return item.assignedDeviceIds.map((entry) => String(entry));
  if (Array.isArray(item?.assignedDevices)) {
    return item.assignedDevices.map((entry) => String(entry?.deviceId || entry?._id || entry?.id || "")).filter(Boolean);
  }
  return [];
};

const normalizeGeofence = (item = {}, index = 0) => {
  const type = item?.type || (item?.radius ? "circle" : "polygon");
  const polygonCoords = Array.isArray(item?.polygon?.coordinates?.[0]) ? item.polygon.coordinates[0] : [];
  const polygonPath = polygonCoords
    .map((coord) => ({
      lat: Number(coord?.[1]),
      lng: Number(coord?.[0]),
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  const center = {
    lat: Number(item?.center?.lat ?? item?.center?.latitude),
    lng: Number(item?.center?.lng ?? item?.center?.longitude),
  };

  return {
    ...item,
    id: String(item?._id || item?.id || `geofence-${index}`),
    name: item?.name || `Geofence ${index + 1}`,
    type,
    category: item?.category || "",
    locationLabel: item?.locationLabel || "",
    notes: item?.notes || item?.description || "",
    center: Number.isFinite(center.lat) && Number.isFinite(center.lng) ? center : null,
    radius: Number(item?.radius || 0),
    polygonPath,
    assignedDeviceIds: normalizeAssignedDeviceIds(item),
    assignedCount: Number(item?.assignedCount ?? normalizeAssignedDeviceIds(item).length),
    isActive: item?.isActive !== false,
    updatedAt: item?.updatedAt || item?.createdAt || null,
  };
};

const buildDraftFromGeofence = (geofence) => {
  if (!geofence) return null;
  if (geofence.type === "circle" && geofence.center) {
    return {
      type: "circle",
      data: {
        center: geofence.center,
        radius: Number(geofence.radius || 0),
      },
    };
  }

  if (geofence.type === "polygon" && geofence.polygonPath.length) {
    return {
      type: "polygon",
      data: geofence.polygonPath,
    };
  }

  return null;
};

const buildPayloadFromDraft = ({ draft, name, category, notes, isActive, locationLabel }) => {
  if (!draft) return null;

  if (draft.type === "circle") {
    return {
      name: name.trim(),
      category: category.trim(),
      notes: notes.trim(),
      type: "circle",
      center: {
        lat: Number(draft.data.center.lat),
        lng: Number(draft.data.center.lng),
      },
      radius: Number(draft.data.radius),
      locationLabel: String(locationLabel || "").trim(),
      isActive,
    };
  }

  const coords = draft.data.map((point) => [Number(point.lng), Number(point.lat)]);
  if (coords.length && (coords[0][0] !== coords[coords.length - 1][0] || coords[0][1] !== coords[coords.length - 1][1])) {
    coords.push(coords[0]);
  }

  return {
    name: name.trim(),
    category: category.trim(),
    notes: notes.trim(),
    type: "polygon",
    polygon: {
      type: "Polygon",
      coordinates: [coords],
    },
    locationLabel: String(locationLabel || "").trim(),
    isActive,
  };
};

const getPolygonCenter = (points = []) => {
  if (!points.length) return DEFAULT_CENTER;
  const sum = points.reduce(
    (acc, point) => ({
      lat: acc.lat + Number(point.lat || 0),
      lng: acc.lng + Number(point.lng || 0),
    }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
};

const arePointsEqual = (left = [], right = []) => {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  return left.every((point, index) => {
    const other = right[index];
    return (
      Math.abs(Number(point?.lat || 0) - Number(other?.lat || 0)) < 1e-7 &&
      Math.abs(Number(point?.lng || 0) - Number(other?.lng || 0)) < 1e-7
    );
  });
};

const areCircleDraftsEqual = (left, right) => {
  if (!left?.data?.center || !right?.data?.center) return false;
  return (
    Math.abs(Number(left.data.center.lat || 0) - Number(right.data.center.lat || 0)) < 1e-7 &&
    Math.abs(Number(left.data.center.lng || 0) - Number(right.data.center.lng || 0)) < 1e-7 &&
    Math.abs(Number(left.data.radius || 0) - Number(right.data.radius || 0)) < 0.1
  );
};

export default function GeofencesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);
  const { showStatus } = useStatus();

  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const { data: geofenceResponse = [], isLoading: geofencesLoading } = useGetSharedGeofencesQuery();
  const { data: devices = [], isLoading: devicesLoading } = useGetAllLiteDevicesQuery();
  const { data: alarms = [] } = useGetAlarmsQuery({ limit: 80, active: true });
  const [createSharedGeofence, { isLoading: creatingGeofence }] = useCreateSharedGeofenceMutation();
  const [updateSharedGeofence, { isLoading: updatingGeofence }] = useUpdateSharedGeofenceMutation();
  const [deleteSharedGeofence, { isLoading: deletingGeofence }] = useDeleteSharedGeofenceMutation();
  const [assignSharedGeofenceDevice] = useAssignSharedGeofenceDeviceMutation();
  const [unassignSharedGeofenceDevice] = useUnassignSharedGeofenceDeviceMutation();

  const mapRef = useRef(null);
  const shapeRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const predictionTimerRef = useRef(null);

  const [search, setSearch] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [selectedGeofenceId, setSelectedGeofenceId] = useState("");
  const [draftGeofence, setDraftGeofence] = useState(null);
  const [drawingMode, setDrawingMode] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [locationLabel, setLocationLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentDraft, setAssignmentDraft] = useState(() => new Set());
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [placePredictions, setPlacePredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [searchMapCenter, setSearchMapCenter] = useState(null);

  const pageMode = location.pathname.endsWith("/create") ? "create" : "list";

  const normalizedGeofences = useMemo(
    () => (Array.isArray(geofenceResponse) ? geofenceResponse : []).map((item, index) => normalizeGeofence(item, index)),
    [geofenceResponse]
  );

  const filteredGeofences = useMemo(() => {
    const query = safeLower(search).trim();
    return normalizedGeofences.filter((item) => {
      const matchesQuery =
        !query ||
        safeLower(item.name).includes(query) ||
        safeLower(item.category).includes(query) ||
        safeLower(item.notes).includes(query);
      const matchesType = typeFilter === "all" || item.type === typeFilter || (typeFilter === "active" && item.isActive) || (typeFilter === "inactive" && !item.isActive);
      return matchesQuery && matchesType;
    });
  }, [normalizedGeofences, search, typeFilter]);

  const selectedGeofence = useMemo(
    () => normalizedGeofences.find((item) => item.id === selectedGeofenceId) || null,
    [normalizedGeofences, selectedGeofenceId]
  );

  useEffect(() => {
    if (!isLoaded || !window.google?.maps) return;
    if (!autocompleteServiceRef.current && window.google.maps.places?.AutocompleteService) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
    if (!geocoderRef.current && window.google.maps.Geocoder) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (pageMode === "create") return;
    if (isCreatingNew) return;
    if (!normalizedGeofences.length) {
      setSelectedGeofenceId("");
      return;
    }
    const exists = normalizedGeofences.some((item) => item.id === selectedGeofenceId);
    if (!exists) {
      setSelectedGeofenceId(normalizedGeofences[0].id);
    }
  }, [isCreatingNew, normalizedGeofences, pageMode, selectedGeofenceId]);

  useEffect(() => {
    const isEditBuilder = pageMode === "create" && Boolean(location.state?.editGeofenceId);
    if (!selectedGeofence || isCreatingNew || (pageMode === "create" && !isEditBuilder)) return;
    setName(selectedGeofence.name || "");
    setCategory(selectedGeofence.category || "");
    setLocationLabel(selectedGeofence.locationLabel || "");
    setNotes(selectedGeofence.notes || "");
    setIsActive(selectedGeofence.isActive !== false);
    setDraftGeofence(buildDraftFromGeofence(selectedGeofence));
    setSearchMapCenter(null);
  }, [isCreatingNew, location.state, pageMode, selectedGeofence]);

  useEffect(() => {
    if (pageMode !== "create") return;
    const editGeofenceId = String(location.state?.editGeofenceId || "");
    if (editGeofenceId) {
      setIsCreatingNew(false);
      setSelectedGeofenceId(editGeofenceId);
      return;
    }
    setIsCreatingNew(true);
    setSelectedGeofenceId("");
    setName("");
    setCategory("");
    setLocationLabel("");
    setNotes("");
    setIsActive(true);
    setSearchMapCenter(null);
  }, [location.state, pageMode]);

  const startNewGeofence = useCallback((shapeType = null) => {
    setIsCreatingNew(true);
    setSelectedGeofenceId("");
    setDraftGeofence(null);
    setName("");
    setCategory("");
    setLocationLabel("");
    setNotes("");
    setIsActive(true);
    setDrawingMode(shapeType);
    setSearchMapCenter(null);
  }, []);

  const handleSelectGeofence = useCallback((geofence) => {
    setIsCreatingNew(false);
    setDrawingMode(null);
    setSelectedGeofenceId(geofence.id);
    setSearchMapCenter(null);
  }, []);

  useEffect(() => {
    if (pageMode !== "create" || !isLoaded) return undefined;
    const raw = String(locationLabel || "").trim();
    const latLngMatch = raw.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);

    if (!raw || latLngMatch || !autocompleteServiceRef.current) {
      setPlacePredictions([]);
      return undefined;
    }

    if (predictionTimerRef.current) {
      clearTimeout(predictionTimerRef.current);
    }

    predictionTimerRef.current = setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: raw,
        },
        (predictions) => {
          setPlacePredictions(Array.isArray(predictions) ? predictions.slice(0, 5) : []);
          setShowPredictions(true);
        }
      );
    }, 180);

    return () => {
      if (predictionTimerRef.current) {
        clearTimeout(predictionTimerRef.current);
        predictionTimerRef.current = null;
      }
    };
  }, [isLoaded, locationLabel, pageMode]);

  const handlePredictionSelect = useCallback((prediction) => {
    if (!prediction?.place_id || !geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: prediction.place_id }, (results, status) => {
      if (status !== "OK" || !Array.isArray(results) || !results[0]) return;
      const result = results[0];
      const lat = result.geometry?.location?.lat?.();
      const lng = result.geometry?.location?.lng?.();
      const nextLabel = result.formatted_address || prediction.description || "";
      setLocationLabel(nextLabel);
      setPlacePredictions([]);
      setShowPredictions(false);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const nextCenter = { lat, lng };
        setSearchMapCenter(nextCenter);
        if (mapRef.current) {
          mapRef.current.panTo(nextCenter);
          mapRef.current.setZoom(15);
        }
      }
    });
  }, []);

  const handleOverlayComplete = useCallback((event) => {
    const overlay = event.overlay;
    if (!overlay) return;

    let nextDraft = null;
    if (event.type === "polygon") {
      const path = overlay.getPath();
      const coords = [];
      for (let index = 0; index < path.getLength(); index += 1) {
        coords.push({
          lat: path.getAt(index).lat(),
          lng: path.getAt(index).lng(),
        });
      }
      nextDraft = { type: "polygon", data: coords };
    }

    if (event.type === "circle") {
      const center = overlay.getCenter();
      nextDraft = {
        type: "circle",
        data: {
          center: { lat: center.lat(), lng: center.lng() },
          radius: overlay.getRadius(),
        },
      };
    }

    overlay.setMap(null);
    setDraftGeofence(nextDraft);
    setDrawingMode(null);
    setIsCreatingNew(true);
  }, []);

  const syncPolygon = useCallback(() => {
    const path = shapeRef.current?.getPath?.();
    if (!path) return;
    const coords = [];
    for (let index = 0; index < path.getLength(); index += 1) {
      coords.push({
        lat: path.getAt(index).lat(),
        lng: path.getAt(index).lng(),
      });
    }
    setDraftGeofence((current) => {
      if (current?.type === "polygon" && arePointsEqual(current.data, coords)) {
        return current;
      }
      return { type: "polygon", data: coords };
    });
  }, []);

  const syncCircle = useCallback(() => {
    const overlay = shapeRef.current;
    if (!overlay?.getCenter) return;
    const center = overlay.getCenter();
    const nextDraft = {
      type: "circle",
      data: {
        center: { lat: center.lat(), lng: center.lng() },
        radius: overlay.getRadius(),
      },
    };
    setDraftGeofence((current) => {
      if (current?.type === "circle" && areCircleDraftsEqual(current, nextDraft)) {
        return current;
      }
      return nextDraft;
    });
  }, []);

  const mapCenter = useMemo(() => {
    if (searchMapCenter?.lat && searchMapCenter?.lng) return searchMapCenter;
    if (draftGeofence?.type === "circle" && draftGeofence.data.center) return draftGeofence.data.center;
    if (draftGeofence?.type === "polygon" && draftGeofence.data.length) return getPolygonCenter(draftGeofence.data);
    if (selectedGeofence?.center) return selectedGeofence.center;
    if (selectedGeofence?.polygonPath?.length) return getPolygonCenter(selectedGeofence.polygonPath);
    const deviceWithLocation = (Array.isArray(devices) ? devices : []).find(
      (device) => Number.isFinite(Number(device?.currentLocation?.latitude)) && Number.isFinite(Number(device?.currentLocation?.longitude))
    );
    if (deviceWithLocation) {
      return {
        lat: Number(deviceWithLocation.currentLocation.latitude),
        lng: Number(deviceWithLocation.currentLocation.longitude),
      };
    }
    return DEFAULT_CENTER;
  }, [devices, draftGeofence, searchMapCenter, selectedGeofence]);

  const handleCenterMap = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.panTo(mapCenter);
    mapRef.current.setZoom(draftGeofence ? 14 : 11);
  }, [draftGeofence, mapCenter]);

  const handleMyLocation = useCallback(() => {
    if (!navigator?.geolocation || !mapRef.current) return;
    navigator.geolocation.getCurrentPosition((position) => {
      const center = {
        lat: Number(position.coords.latitude),
        lng: Number(position.coords.longitude),
      };
      mapRef.current.panTo(center);
      mapRef.current.setZoom(15);
    });
  }, []);

  const handleUndo = useCallback(() => {
    if (draftGeofence?.type === "polygon" && draftGeofence.data.length > 0) {
      setDraftGeofence((current) => ({
        ...current,
        data: current.data.slice(0, -1),
      }));
      return;
    }
    setDraftGeofence(null);
  }, [draftGeofence]);

  const handleClearDraft = useCallback(() => {
    setDraftGeofence(null);
    setDrawingMode(null);
    shapeRef.current = null;
  }, []);

  const saveDisabled = !name.trim() || !draftGeofence || creatingGeofence || updatingGeofence;

  const handleSaveGeofence = useCallback(async () => {
    const payload = buildPayloadFromDraft({ draft: draftGeofence, name, category, notes, isActive, locationLabel });
    if (!payload) {
      showStatus("warning", "Draw a circle or polygon before saving the geofence.");
      return;
    }

    try {
      if (selectedGeofence && !isCreatingNew) {
        await updateSharedGeofence({ geofenceId: selectedGeofence.id, ...payload }).unwrap();
        showStatus("success", "Geofence updated successfully.");
      } else {
        const created = await createSharedGeofence(payload).unwrap();
        const createdId = String(created?._id || created?.id || created?.data?._id || created?.data?.id || "");
        if (createdId) setSelectedGeofenceId(createdId);
        setIsCreatingNew(false);
        showStatus("success", "Geofence created successfully.");
        if (pageMode === "create") {
          navigate("/geofences/list");
        }
      }
    } catch (error) {
      showStatus("error", error?.data?.message || error?.data?.msg || "Unable to save geofence.");
    }
  }, [
    category,
    createSharedGeofence,
    draftGeofence,
    isActive,
    isCreatingNew,
    name,
    notes,
    navigate,
    pageMode,
    locationLabel,
    selectedGeofence,
    showStatus,
    updateSharedGeofence,
  ]);

  const handleDelete = useCallback(async (targetGeofence = selectedGeofence) => {
    if (!targetGeofence) return;
    if (!window.confirm(`Delete geofence "${targetGeofence.name}"?`)) return;

    try {
      await deleteSharedGeofence(targetGeofence.id).unwrap();
      setSelectedGeofenceId("");
    setDraftGeofence(null);
    setName("");
    setCategory("");
    setLocationLabel("");
    setNotes("");
    setIsCreatingNew(false);
      showStatus("success", "Geofence deleted successfully.");
    } catch (error) {
      showStatus("error", error?.data?.message || error?.data?.msg || "Unable to delete geofence.");
    }
  }, [deleteSharedGeofence, selectedGeofence, showStatus]);

  const filteredDevices = useMemo(() => {
    const query = safeLower(deviceSearch).trim();
    const sourceDevices = Array.isArray(devices) ? devices : [];
    return sourceDevices
      .filter((device) => {
        if (!query) return true;
        return (
          safeLower(device?.name).includes(query) ||
          safeLower(device?.vehicleNumber).includes(query) ||
          safeLower(device?.imei).includes(query) ||
          safeLower(device?.model).includes(query)
        );
      })
      .map((device) => ({
        ...device,
        status: getCanonicalVehicleStatus(device),
      }));
  }, [deviceSearch, devices]);

  const geofenceAlarms = useMemo(
    () => (Array.isArray(alarms) ? alarms : []).filter((item) => GEOFENCE_ALARM_TYPES.includes(String(item?.alarmType || item?.eventType || "").toLowerCase())),
    [alarms]
  );

  const assignedIdSet = useMemo(
    () => new Set((selectedGeofence?.assignedDeviceIds || []).map((value) => String(value))),
    [selectedGeofence?.assignedDeviceIds]
  );

  const openAssignmentModal = useCallback((geofence = selectedGeofence) => {
    if (!geofence?.id) {
      showStatus("warning", "Save the geofence first, then assign devices.");
      return;
    }
    setSelectedGeofenceId(geofence.id);
    setAssignmentDraft(new Set((geofence.assignedDeviceIds || []).map((value) => String(value))));
    setAssignmentOpen(true);
  }, [selectedGeofence, showStatus]);

  const closeAssignmentModal = useCallback(() => {
    setAssignmentOpen(false);
    setAssignmentDraft(new Set());
    setDeviceSearch("");
  }, []);

  const toggleAssignmentDraft = useCallback((deviceId, checked) => {
    setAssignmentDraft((current) => {
      const next = new Set(current);
      if (checked) next.add(String(deviceId));
      else next.delete(String(deviceId));
      return next;
    });
  }, []);

  const saveAssignments = useCallback(async () => {
    if (!selectedGeofence?.id) return;
    const original = new Set((selectedGeofence.assignedDeviceIds || []).map((value) => String(value)));
    const next = assignmentDraft;
    const toAssign = [...next].filter((id) => !original.has(id));
    const toRemove = [...original].filter((id) => !next.has(id));

    setAssignmentSaving(true);
    try {
      for (const deviceId of toAssign) {
        await assignSharedGeofenceDevice({ geofenceId: selectedGeofence.id, deviceId }).unwrap();
      }
      for (const deviceId of toRemove) {
        await unassignSharedGeofenceDevice({ geofenceId: selectedGeofence.id, deviceId }).unwrap();
      }
      showStatus("success", "Vehicle assignments updated.");
      closeAssignmentModal();
    } catch (error) {
      showStatus("error", error?.data?.message || error?.data?.msg || "Unable to update device assignment.");
    } finally {
      setAssignmentSaving(false);
    }
  }, [assignSharedGeofenceDevice, assignmentDraft, closeAssignmentModal, selectedGeofence, showStatus, unassignSharedGeofenceDevice]);

  const handleViewGeofence = useCallback((geofence) => {
    handleSelectGeofence(geofence);
    setTimeout(() => {
      const target = geofence?.center || (geofence?.polygonPath?.length ? getPolygonCenter(geofence.polygonPath) : null);
      if (target && mapRef.current) {
        mapRef.current.panTo(target);
        mapRef.current.setZoom(14);
      }
    }, 0);
  }, [handleSelectGeofence]);

  const handleEditGeofence = useCallback((geofence) => {
    handleSelectGeofence(geofence);
    setDraftGeofence(buildDraftFromGeofence(geofence));
    setName(geofence.name || "");
    setCategory(geofence.category || "");
    setNotes(geofence.notes || "");
    setIsActive(geofence.isActive !== false);
    navigate("/geofences/create", { state: { editGeofenceId: geofence.id } });
  }, [handleSelectGeofence, navigate]);

  const sidebarItems = useGlobalSidebarItems();

  const renderMapCanvas = ({ compact = false, previewOnly = false } = {}) => (
    <div className={compact ? "geofence-preview-map" : "geofence-create-map"}>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={draftGeofence ? 13 : 2}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: !compact,
            gestureHandling: "greedy",
            zoomControl: !compact,
            draggable: !previewOnly,
          }}
        >
          {!previewOnly ? (
            <DrawingManager
              drawingMode={drawingMode}
              onOverlayComplete={handleOverlayComplete}
              options={{
                drawingControl: false,
                polygonOptions: SHAPE_OPTIONS,
                circleOptions: SHAPE_OPTIONS,
              }}
            />
          ) : null}

          {draftGeofence?.type === "polygon" && draftGeofence.data.length ? (
            <Polygon
              key={`draft-polygon-${selectedGeofence?.id || "new"}`}
              paths={draftGeofence.data}
              options={{ ...SHAPE_OPTIONS, editable: !previewOnly, draggable: !previewOnly }}
              onLoad={(polygon) => {
                shapeRef.current = polygon;
              }}
              onMouseUp={previewOnly ? undefined : syncPolygon}
              onDragEnd={previewOnly ? undefined : syncPolygon}
            />
          ) : null}

          {draftGeofence?.type === "circle" && draftGeofence.data.center ? (
            <GoogleCircle
              key={`draft-circle-${selectedGeofence?.id || "new"}`}
              center={draftGeofence.data.center}
              radius={Number(draftGeofence.data.radius || 0)}
              options={{ ...SHAPE_OPTIONS, editable: !previewOnly, draggable: !previewOnly }}
              onLoad={(circle) => {
                shapeRef.current = circle;
              }}
              onDragEnd={previewOnly ? undefined : syncCircle}
              onCenterChanged={previewOnly ? undefined : syncCircle}
              onRadiusChanged={previewOnly ? undefined : syncCircle}
            />
          ) : null}
        </GoogleMap>
      ) : (
        <div className="geofence-empty-state">Loading Google Map…</div>
      )}
    </div>
  );

  const shell = (content) => (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={sidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />
      <main className="dashboard-content">
        {content}
      </main>
    </div>
  );

  if (pageMode === "create") {
    return shell(
      <div className="geofence-builder-page">
        <section className="geofence-builder-card">
          <div className="geofence-builder-header">
            <div>
              <p className="geofence-kicker">Geofences / Create New Geofence</p>
              <h1>Create Geofence</h1>
            </div>
            <button type="button" className="geofence-secondary-btn" onClick={() => navigate("/geofences/list")}>Geofence List</button>
          </div>

          <div className="geofence-builder-form">
            <label>
              <span>Geofence Name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Warehouse Delhi" />
            </label>
            <label>
              <span>Category</span>
              <input value={category} onChange={(event) => setCategory(event.target.value)} placeholder="Warehouse" />
            </label>
            <label>
              <span>Status</span>
              <select value={isActive ? "active" : "inactive"} onChange={(event) => setIsActive(event.target.value === "active")}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label className="geofence-location-field">
              <span>Search Place</span>
              <input
                value={locationLabel}
                onChange={(event) => {
                  setLocationLabel(event.target.value);
                  setShowPredictions(true);
                }}
                onFocus={() => setShowPredictions(true)}
                placeholder="Search with Google Maps place suggestions"
              />
              {showPredictions && placePredictions.length ? (
                <div className="geofence-predictions-card">
                  {placePredictions.map((prediction) => (
                    <button
                      key={prediction.place_id}
                      type="button"
                      className="geofence-prediction-item"
                      onClick={() => handlePredictionSelect(prediction)}
                    >
                      <strong>{prediction.structured_formatting?.main_text || prediction.description}</strong>
                      <span>{prediction.structured_formatting?.secondary_text || ""}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </label>
          </div>
        </section>

        <section className="geofence-map-workspace">
          <div className="geofence-floating-toolbar">
            <button type="button" className={drawingMode === "polygon" ? "active" : ""} onClick={() => setDrawingMode("polygon")}><Pentagon size={18} /> Draw Polygon</button>
            <button type="button" className={drawingMode === "circle" ? "active" : ""} onClick={() => setDrawingMode("circle")}><Circle size={18} /> Draw Circle</button>
            <button type="button" onClick={handleUndo}><Crosshair size={18} /> Undo</button>
            <button type="button" onClick={handleClearDraft}><Trash2 size={18} /> Clear</button>
            <button type="button" onClick={handleCenterMap}><MapPinned size={18} /> Center Map</button>
            <button type="button" onClick={handleMyLocation}><Radar size={18} /> My Location</button>
          </div>

          {renderMapCanvas()}

          <button type="button" className="geofence-save-floating" onClick={handleSaveGeofence} disabled={saveDisabled}>
            <ShieldAlert size={18} /> Save Geofence
          </button>
        </section>
      </div>
    );
  }

  const selectedInfo = selectedGeofence || filteredGeofences[0] || null;
  const entryCount = geofenceAlarms.filter((alarm) => String(alarm?.alarmType || alarm?.eventType || "").toLowerCase().includes("entry") || String(alarm?.alarmType || alarm?.eventType || "").toLowerCase().includes("enter")).length;
  const exitCount = geofenceAlarms.filter((alarm) => String(alarm?.alarmType || alarm?.eventType || "").toLowerCase().includes("exit")).length;
  const violations = geofenceAlarms.filter((alarm) => String(alarm?.alarmType || alarm?.eventType || "").toLowerCase().includes("violation")).length;

  return shell(
    <div className="geofence-list-page">
      <section className="geofence-list-toolbar-card">
        <div>
          <p className="geofence-kicker">Geofences / Geofence Management</p>
          <h1>Geofence List</h1>
          <p>Manage, edit, assign vehicles, and preview existing geofence zones.</p>
        </div>
        <div className="geofence-list-tools">
          <div className="geofence-search-input"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search geofences..." /></div>
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="all">All</option>
            <option value="polygon">Polygon</option>
            <option value="circle">Circle</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button type="button" className="geofence-primary-btn" onClick={() => navigate("/geofences/create")}><Plus size={16} /> Create New</button>
        </div>
      </section>

      <section className="geofence-management-grid">
        <div className="geofence-table-card">
          <div className="geofence-table-scroll">
            <table className="geofence-table">
              <thead>
                <tr>
                  <th>Geofence Name</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Assigned Vehicles</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {geofencesLoading ? (
                  <tr><td colSpan="7">Loading geofences…</td></tr>
                ) : filteredGeofences.length ? filteredGeofences.map((geofence) => (
                  <tr key={geofence.id} className={selectedGeofenceId === geofence.id ? "selected" : ""} onClick={() => handleSelectGeofence(geofence)}>
                    <td><strong>{geofence.name}</strong><span>{geofence.notes || geofence.category || "No description"}</span></td>
                    <td><span className="geofence-pill blue">{formatAlarmLabel(geofence.type)}</span></td>
                    <td>{geofence.category || "-"}</td>
                    <td><span className={`geofence-pill ${geofence.isActive ? "green" : "gray"}`}>{geofence.isActive ? "Active" : "Inactive"}</span></td>
                    <td>{geofence.assignedCount || 0}</td>
                    <td>{formatDateTime(geofence.createdAt || geofence.updatedAt)}</td>
                    <td>
                      <div className="geofence-row-actions" onClick={(event) => event.stopPropagation()}>
                        <button type="button" onClick={() => handleViewGeofence(geofence)}>View</button>
                        <button type="button" onClick={() => handleEditGeofence(geofence)}>Edit</button>
                        <button type="button" onClick={() => openAssignmentModal(geofence)}>Assign</button>
                        <button type="button" className="danger" onClick={() => handleDelete(geofence)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="7">No geofences found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="geofence-info-card">
          {selectedInfo ? (
            <>
              <div className="geofence-info-header">
                <div>
                  <h2>{selectedInfo.name}</h2>
                  <p>{selectedInfo.category || "Geofence"}</p>
                </div>
                <span className={`geofence-pill ${selectedInfo.isActive ? "green" : "gray"}`}>{selectedInfo.isActive ? "Active" : "Inactive"}</span>
              </div>

              <div className="geofence-info-grid">
                <div><span>Type</span><strong>{formatAlarmLabel(selectedInfo.type)}</strong></div>
                <div><span>Category</span><strong>{selectedInfo.category || "-"}</strong></div>
                <div><span>Area</span><strong>{selectedInfo.type === "circle" ? `${Number(selectedInfo.radius || 0).toFixed(0)} m radius` : `${selectedInfo.polygonPath.length} points`}</strong></div>
                <div><span>Created Date</span><strong>{formatDateTime(selectedInfo.createdAt)}</strong></div>
                <div><span>Last Updated</span><strong>{formatDateTime(selectedInfo.updatedAt)}</strong></div>
                <div><span>Assigned Vehicles Count</span><strong>{selectedInfo.assignedCount || 0}</strong></div>
                <div><span>Entry Count</span><strong>{entryCount}</strong></div>
                <div><span>Exit Count</span><strong>{exitCount}</strong></div>
                <div><span>Violations</span><strong className="danger-text">{violations}</strong></div>
              </div>

              <h3>Geofence Preview</h3>
              {renderMapCanvas({ compact: true, previewOnly: true })}

              <div className="geofence-info-actions">
                <button type="button" className="geofence-primary-btn" onClick={() => openAssignmentModal(selectedInfo)}>Assign Vehicles</button>
                <button type="button" className="geofence-secondary-btn" onClick={() => handleEditGeofence(selectedInfo)}>Edit Geofence</button>
                <button type="button" className="geofence-danger-btn" onClick={() => handleDelete(selectedInfo)}>Delete Geofence</button>
              </div>
            </>
          ) : (
            <div className="geofence-empty-state">Select a geofence to view details.</div>
          )}
        </aside>
      </section>

      {assignmentOpen ? (
        <div className="geofence-modal-backdrop" onClick={closeAssignmentModal}>
          <div className="geofence-assignment-modal" onClick={(event) => event.stopPropagation()}>
            <div className="geofence-assignment-head">
              <div><h2>Assign Vehicles</h2><p>{selectedGeofence?.name || "Selected geofence"}</p></div>
              <button type="button" onClick={closeAssignmentModal}>×</button>
            </div>
            <div className="geofence-search-input"><Search size={16} /><input value={deviceSearch} onChange={(event) => setDeviceSearch(event.target.value)} placeholder="Search vehicles" /></div>
            <div className="geofence-assignment-columns">
              <div><h3>Available Vehicles</h3>{filteredDevices.filter((device) => !assignmentDraft.has(String(device?._id || ""))).map((device) => <button key={device._id} type="button" onClick={() => toggleAssignmentDraft(device._id, true)}>{device.vehicleNumber || device.name || device.imei}<span>{device.imei}</span></button>)}</div>
              <div><h3>Assigned Vehicles</h3>{filteredDevices.filter((device) => assignmentDraft.has(String(device?._id || ""))).map((device) => <button key={device._id} type="button" onClick={() => toggleAssignmentDraft(device._id, false)}>{device.vehicleNumber || device.name || device.imei}<span>Remove</span></button>)}</div>
            </div>
            <div className="geofence-assignment-actions">
              <button type="button" className="geofence-secondary-btn" onClick={closeAssignmentModal}>Cancel</button>
              <button type="button" className="geofence-primary-btn" onClick={saveAssignments} disabled={assignmentSaving}>{assignmentSaving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
